import React, { useState } from 'react';
import classNames from 'classnames';
import loadable from '@loadable/component';
import { useHistory, useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { isAiConciergeEnabled } from '../../util/aiConcierge';
import CategoryShortcuts from '../../components/CategoryShortcuts/CategoryShortcuts';
import UnifiedSearch from '../../components/UnifiedSearch/UnifiedSearch';
import { isUnifiedSearchEnabled } from '../../util/unifiedSearch';

import SectionLogoSlider from '../PageBuilder/SectionBuilder/SectionLogoSlider';
import SectionSpaceOwner from '../PageBuilder/SectionBuilder/SectionSpaceOwner';
import SectionFaq from '../PageBuilder/SectionBuilder/SectionFaq';
import SectionLandingListingRows from '../PageBuilder/SectionBuilder/SectionLandingListingRows/SectionLandingListingRows';
import SectionLocations from '../PageBuilder/SectionBuilder/SectionLocations';
import SectionAirportLounges from '../PageBuilder/SectionBuilder/SectionAirportLounges';

import HeroSearchForm from './HeroSearchForm';
import SearchAiConcierge from '../SearchPage/SearchAiConcierge/SearchAiConcierge';
import css from './FallbackPage.module.css';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

const is404 = error => error?.status === 404;

// Default landing sections: clear white / light-grey alternation (sticky bar stays white)
const defaultLandingSections = () => [
  { sectionType: 'defaultLandingHero', sectionId: 'hero' },
  { sectionType: 'logoSlider', sectionId: 'logo-slider', landingSurface: 'muted' },
  {
    sectionType: 'landingListingRows',
    sectionId: 'landing-listings-all',
    // 'workation' and 'hourly-rental' were removed: neither exists in the hosted category tree,
    // so both rows resolved to nothing and could never render. 'coliving' and 'newest' followed
    // in the journey simplification: five carousels with ~15 controls between the hero and the
    // city cards buried the one decision the visitor came for (day pass, meeting room, fixed desk).
    rowIds: ['tagespass', 'meeting', 'fixdesk'],
    landingSurface: 'white',
  },
  // The lounge banner that used to follow this carousel was removed: both blocks said the same
  // thing back to back (961px on mobile for one message). The carousel wins because it shows real
  // bookable cards with prices, and its "Alle anzeigen" still leads to the lounge hub — the banner's
  // concrete claims (290+ lounges, no membership) moved into SectionAirportLounges.subtitle.
  //
  // The category-shortcut tiles were removed too: they mapped the same ten categories to the same
  // SearchPage URLs as the category bar above, for 1104px of desktop height.
  { sectionType: 'airportLounges', sectionId: 'airport-lounges', landingSurface: 'muted' },
  { sectionType: 'sectionLocations', sectionId: 'locations', landingSurface: 'white' },
  { sectionType: 'sectionSpaceOwner', sectionId: 'space-owner', landingSurface: 'muted' },
  { sectionType: 'faq', sectionId: 'faq', landingSurface: 'white' },
];

/** Page data for the custom start page (hero + categories). Exported for use in LandingPage. */
export const getDefaultLandingPageData = () => ({
  sections: defaultLandingSections(),
  meta: {
    pageTitle: {
      fieldType: 'metaTitle',
      content: 'Coworking Spaces in der Schweiz flexibel buchen | cowork24',
    },
    // ≤155 characters, so the snippet is not cut off (the previous text ran to 172).
    pageDescription: {
      fieldType: 'metaDescription',
      content:
        'Coworking Spaces, Meetingräume und Büros in der Schweiz stunden-, tage- oder monatsweise mieten. Preise vergleichen und direkt online buchen.',
    },
  },
});

// Create fallback content (array of sections) in page asset format:
export const fallbackSections = error => {
  if (is404(error)) {
    return getDefaultLandingPageData();
  }
  return {
    sections: [
      {
        sectionType: 'customMaintenance',
        sectionId: 'maintenance-mode',
        error,
      },
    ],
    meta: {
      pageTitle: {
        fieldType: 'metaTitle',
        content: 'Wartung – cowork24',
      },
      pageDescription: {
        fieldType: 'metaDescription',
        content: 'Die Seite wird gerade gewartet. Bitte versuche es in Kürze erneut.',
      },
    },
  };
};

/**
 * Segmented pill switch between the AI concierge and the traditional
 * location+dates form. Only rendered when the concierge itself is enabled —
 * with the feature off there is nothing to switch to, so the hero falls back
 * to the plain form exactly as before this feature existed.
 */
const SearchModeToggle = ({ mode, onChange, intl }) => (
  <div
    className={css.searchModeToggle}
    role="group"
    aria-label={intl.formatMessage({ id: 'LandingPage.searchModeToggleLabel' })}
  >
    <button
      type="button"
      className={classNames(css.searchModeButton, {
        [css.searchModeButtonActive]: mode === 'ai',
      })}
      aria-pressed={mode === 'ai'}
      onClick={() => onChange('ai')}
    >
      <Sparkles className={css.searchModeIcon} size={14} strokeWidth={2} aria-hidden="true" />
      <FormattedMessage id="LandingPage.searchModeAi" />
    </button>
    <button
      type="button"
      className={classNames(css.searchModeButton, {
        [css.searchModeButtonActive]: mode === 'classic',
      })}
      aria-pressed={mode === 'classic'}
      onClick={() => onChange('classic')}
    >
      <FormattedMessage id="LandingPage.searchModeClassic" />
    </button>
  </div>
);

/**
 * Default hero section (Airbnb-style) with headline and floating search bar.
 * Exported so LandingPage can use it for the custom start page.
 */
export const SectionDefaultLandingHero = props => {
  const { sectionId } = props;
  const config = useConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  // Defaults to the AI concierge — the flagship entry point while the feature
  // is opt-in per stage; the traditional form is one click away, never removed.
  const [searchMode, setSearchMode] = useState('ai');
  const conciergeAvailable = isAiConciergeEnabled();

  return (
    <section id={sectionId} className={css.heroSection}>
      <div className={css.heroOrbs} aria-hidden="true">
        <div className={classNames(css.heroOrb, css.heroOrbCoral)} />
        <div className={classNames(css.heroOrb, css.heroOrbBrand)} />
        <div className={classNames(css.heroOrb, css.heroOrbSlate)} />
      </div>
      <div className={css.heroContent}>
        <h1 className={css.heroTitle}>
          <FormattedMessage id="LandingPage.defaultHeroTitle" />
        </h1>
        <p className={css.heroDescription}>
          <FormattedMessage id="LandingPage.defaultHeroDescription" />
        </p>
        {isUnifiedSearchEnabled() ? (
          // One element for every entry point (docs/unified-search/spec.md): Wo · Raum, the
          // concierge inside the Wo field, the space types inside "Raum" — so neither the KI/Klassisch
          // toggle nor the shortcuts below are needed.
          <UnifiedSearch placement="hero" className={css.heroUnifiedSearch} />
        ) : conciergeAvailable ? (
          <>
            <SearchModeToggle mode={searchMode} onChange={setSearchMode} intl={intl} />
            {searchMode === 'ai' ? (
              // A successful concierge search navigates straight to /s, so its
              // reply state (see SearchAiConcierge.module.css) is only ever
              // visible on SearchPage itself, not here — pre-existing routing
              // behavior, not something this toggle changes.
              <SearchAiConcierge
                className={css.heroConcierge}
                config={config}
                intl={intl}
                history={history}
                routeConfiguration={routeConfiguration}
                location={location}
              />
            ) : (
              <HeroSearchForm />
            )}
          </>
        ) : (
          <HeroSearchForm />
        )}
        {/* The same three space types as the header filter, as one-click shortcuts. A visitor who
            already knows what kind of room they want should not have to describe it to the
            concierge or open a filter first. Built on the filter's own group definitions, so the
            two cannot drift apart. */}
        {isUnifiedSearchEnabled() ? null : <CategoryShortcuts className={css.heroShortcuts} />}
        {/* No supply-side link here on purpose: the hero belongs to the visitor who wants to
            book. "Space anbieten" stays in the topbar, the footer and SectionSpaceOwner below. */}
      </div>
    </section>
  );
};

// Note: this microcopy/translation does not come from translation file.
//       It needs to be something that is not part of fetched assets but built-in text
const SectionMaintenanceMode = props => {
  const { sectionId, error } = props;

  return (
    <section id={sectionId} className={css.root}>
      <div className={css.content}>
        <h2>Oops, something went wrong!</h2>
        <p>{error?.message}</p>
      </div>
    </section>
  );
};

// This is the fallback page, in case there's no Landing Page asset defined in Console.
const FallbackPage = props => {
  const { error, ...rest } = props;
  return (
    <PageBuilder
      pageAssetsData={fallbackSections(error)}
      options={{
        sectionComponents: {
          customMaintenance: { component: SectionMaintenanceMode },
          defaultLandingHero: { component: SectionDefaultLandingHero },
          landingListingRows: { component: SectionLandingListingRows },
          logoSlider: { component: SectionLogoSlider },
          airportLounges: { component: SectionAirportLounges },
          sectionLocations: { component: SectionLocations },
          sectionSpaceOwner: { component: SectionSpaceOwner },
          faq: { component: SectionFaq },
        },
      }}
      {...rest}
    />
  );
};

export default FallbackPage;
